import { BrowserStorageLocalServiceInterface } from 'pf-frontend-common/src/service/browser-storage-local/service.interface';

import { BrokerClientTypeEnum } from 'common/data/broker/client-type.enum';
import { BrokerModel } from 'common/data/broker/model';
import { BrokerLeadModel } from 'common/data/broker-lead/model';
import { CountryCodeEnum } from 'common/data/country/code.enum';
import { LeadEnquiryTypeEnum } from 'common/data/lead/enquiry-type.enum';
import { LeadModelInterface } from 'common/data/lead/model.interface';
import { UserModel } from 'common/data/user/model';
import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorMinLength } from 'common/module/input/validator/min-length';
import { InputValidatorPhone } from 'common/module/input/validator/phone';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { LeadEmailFormContactInputFieldNameEnum } from 'common/module/lead/email/form/contact/input-field-name.enum';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LeadEmailFormStrategyInterface } from 'common/module/lead/email/form/strategy.interface';
import { LeadEmailFormStrategyAdapterInterface } from 'common/module/lead/email/form/strategy/adapter.interface';
import { LeadEmailFormStrategyOptionsInterface } from 'common/module/lead/email/form/strategy/options.interface';
import { LeadEmailFormStrategyStateInterface } from 'common/module/lead/email/form/strategy/state.interface';
import { LeadEmailFormStrategyStoreInterface } from 'common/module/lead/email/form/strategy/store.interface';
import { LeadEmailFormContactServiceInterface } from 'common/service/lead-email-form-contact/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { LeadEmailFormStrategyStorageKeyEnum } from './storage-key.enum';

export class LeadEmailFormStrategyBroker implements LeadEmailFormStrategyInterface {
  /**
   * Minimum characters limit for message
   */
  public static readonly MIN_MESSAGE_CHARACTERS_LIMIT: number = 5;
  /**
   * Local storage key
   */
  private localStorageKey: LeadEmailFormStrategyStorageKeyEnum = LeadEmailFormStrategyStorageKeyEnum.broker;

  /**
   * Model
   */
  private model: BrokerModel;

  /**
   * Constructor
   */
  constructor(
    public store: LeadEmailFormStrategyStoreInterface,
    public adapter: LeadEmailFormStrategyAdapterInterface,
    private browserStorageLocalService: BrowserStorageLocalServiceInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private leadEmailFormContactService: LeadEmailFormContactServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: LeadEmailFormStrategyOptionsInterface): void {
    this.model = <BrokerModel>options.model;
    this.setUserData();
  }

  /**
   * @inheritDoc
   */
  public getInitialState(): LeadEmailFormStrategyStateInterface {
    const historyModel = <BrokerLeadModel>this.browserStorageLocalService.getData(this.localStorageKey);

    const state: LeadEmailFormStrategyStateInterface = {
      input: {
        name: {
          value:
            this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.name) ||
            (historyModel ? historyModel.name : ''),
          placeholder: i18nTranslate('Your name'),
          validators: [new InputValidatorRequired(i18nTranslate('Please enter your name'))],
        },
        message: {
          value: '',
          placeholder: i18nTranslate('Your message'),
          validators: [
            new InputValidatorRequired(i18nTranslate('Please enter a message')),
            new InputValidatorMinLength(
              i18nTranslate('email-broker:message:validation:character_limit'),
              LeadEmailFormStrategyBroker.MIN_MESSAGE_CHARACTERS_LIMIT
            ),
          ],
        },
        email: {
          value:
            this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.email) ||
            (historyModel ? historyModel.email : ''),
          placeholder: i18nTranslate('Your email'),
          validators: [
            new InputValidatorRequired(i18nTranslate('Please enter your email')),
            new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
          ],
        },
        phone: {
          value:
            this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.phone) ||
            (historyModel ? historyModel.phone : ''),
          placeholder: i18nTranslate('Your phone'),
          validators: [
            new InputValidatorRequired(i18nTranslate('Please enter your phone number')),
            new InputValidatorPhone(i18nTranslate('Please enter a valid phone number')),
          ],
          countryCode: this.getInitialCountryCode(),
        },
        enquiryType: {
          value:
            <LeadEnquiryTypeEnum>(
              this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.enquiryType)
            ) || (historyModel ? historyModel.enquiry_type : LeadEnquiryTypeEnum.none),
          validators: [new InputValidatorRequired(i18nTranslate('Select enquiry type'))],
          options: [
            {
              value: LeadEnquiryTypeEnum.none,
              text: i18nTranslate('Select enquiry type'),
            },
            {
              value: LeadEnquiryTypeEnum.toBuy,
              text: i18nTranslate('I am looking to buy'),
            },
            {
              value: LeadEnquiryTypeEnum.toRent,
              text: i18nTranslate('I am looking to rent'),
            },
            {
              value: LeadEnquiryTypeEnum.toSell,
              text: i18nTranslate('I have a property to sell'),
            },
            {
              value: LeadEnquiryTypeEnum.toLet,
              text: i18nTranslate('I have a property to let'),
            },
            {
              value: LeadEnquiryTypeEnum.other,
              text: i18nTranslate('Other'),
            },
          ],
        },
        captcha: {
          validators: [],
          value: null,
        },
      },
      status: {
        submitting: false,
        submitted: false,
      },
      formTitle:
        this.model.client_type === BrokerClientTypeEnum.developer
          ? i18nTranslate('Email developer')
          : i18nTranslate('Email broker'),
      error: '',
    };

    if (this.isCountryMorocco()) {
      state.input.acceptTerms = {
        value: historyModel ? historyModel.acceptTerms : false,
        validators: [new InputValidatorRequired(i18nTranslate('Please accept the following conditions:'))],
      };

      state.input.receiveAdvertising = {
        value: historyModel ? historyModel.receiveAdvertising : false,
        validators: [new InputValidatorRequired(i18nTranslate('Please accept the following conditions:'))],
      };

      if (historyModel) {
        state.input.acceptTerms.value = historyModel.acceptTerms;
        state.input.receiveAdvertising.value = historyModel.receiveAdvertising;
      }
    }

    return state;
  }

  /**
   * @inheritDoc
   */
  public updateInput(input: LeadEmailFormInputInterface): void {
    const model = <LeadModelInterface>this.adapter.createModelFromLeadEmailForm(input);

    const historyModel = {
      name: model.name,
      email: model.email,
      enquiry_type: model.enquiry_type,
      phone: model.phone,
      countryCode: input.phone.countryCode,
      acceptTerms: false,
      receiveAdvertising: false,
    };

    if (this.isCountryMorocco()) {
      historyModel.acceptTerms = model.acceptTerms;
      historyModel.receiveAdvertising = model.receiveAdvertising;
    }

    this.browserStorageLocalService.setData(this.localStorageKey, historyModel);
  }

  /**
   * @inheritDoc
   */
  public setUserData(): void {
    const userModel = <UserModel>this.userAuthenticationService.getUser();
    const brokerLeadModel = this.browserStorageLocalService.getData(this.localStorageKey);
    let historyModel;

    if (!userModel) {
      return;
    }

    if (!brokerLeadModel) {
      historyModel = {
        name: userModel.first_name + ' ' + userModel.last_name,
        email: userModel.email,
        createAlert: false,
        phone: '',
        acceptTerms: false,
        receiveAdvertising: false,
        enquiry_type: null,
      };
    } else {
      historyModel = brokerLeadModel;
    }

    this.browserStorageLocalService.setData(this.localStorageKey, historyModel);
  }

  /**
   * @inheritDoc
   */
  public getInitialCountryCode(): string {
    const brokerLeadModel = <LeadModelInterface>this.browserStorageLocalService.getData(this.localStorageKey);
    return (
      this.leadEmailFormContactService.getData(LeadEmailFormContactInputFieldNameEnum.countryCode) ||
      (brokerLeadModel && brokerLeadModel.countryCode ? brokerLeadModel.countryCode : configGetCountry().code)
    );
  }

  /**
   * Check if country is Morocco
   */
  private isCountryMorocco(): boolean {
    return configGetCountry().code === CountryCodeEnum.ma;
  }
}
